import { ApolloError } from "@apollo/client"
import { withTrailingSlash } from "@pathwright/utils"
import {
  getUrlFromLinkTo,
  LinkTo
} from "@pathwright/ux/src/components/link/Link"
import { useCallback, useEffect, useState } from "react"
import { ResourceBackgroundType } from "@pathwright/graphql-client/src/generated"
import { AUGMENTATION_PANEL_QUERY_PARAM } from "../../utils/urls"
import {
  COVER_PAGE_HASH_SECTION_KEYS,
  COVER_PAGE_PANEL_ROOT_KEY,
  COVER_PAGE_PANEL_SEARCH_DEBOUNCE_MS
} from "../constants"
import {
  CoverPageHashSectionKey,
  CoverPagePanelSection,
  CoverPagePanelSectionKey,
  toCoverPageScrollHashKey
} from "../types"
import { useCoverPageTranslate } from "../utils"

function getCoverPagePanelErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApolloError) {
    // Get first message from graphql errors
    const graphQLErrorMessage = error.graphQLErrors.find(
      (graphQLError) => graphQLError.message
    )?.message

    return graphQLErrorMessage || error.message || fallback
  }

  if (error instanceof Error) {
    return error.message || fallback
  }

  return fallback
}

export function useCoverPagePanelErrorMessage() {
  const { t } = useCoverPageTranslate()

  return useCallback(
    (error: unknown, contextMessage = "") => {
      const message = getCoverPagePanelErrorMessage(
        error,
        t("panel_errors.fallback", {
          defaultValue: "Unable to save your changes."
        })
      )

      return `${contextMessage} ${message}`.trim()
    },
    [t]
  )
}

export const COVER_PAGE_BACKGROUND_TYPE_INPUT = {
  ambient: 5,
  pristine: 1
} as const

export function getCoverPageBackgroundTypeInput(
  isAmbient: boolean
): (typeof COVER_PAGE_BACKGROUND_TYPE_INPUT)[keyof typeof COVER_PAGE_BACKGROUND_TYPE_INPUT] {
  return isAmbient
    ? COVER_PAGE_BACKGROUND_TYPE_INPUT.ambient
    : COVER_PAGE_BACKGROUND_TYPE_INPUT.pristine
}

export function getCoverPageAmbientResourceFields(isAmbient: boolean) {
  return {
    background_type: isAmbient
      ? ResourceBackgroundType.Blur
      : ResourceBackgroundType.Pristine,
    is_ambient_background: isAmbient
  }
}

type CoverPageBackgroundResource = {
  background_image_raw?: string | null
  background_image?: string | null
  cover_background_image?: string | null
  pristine_background?: string | null
  blurred_background?: string | null
  cover_blurred_background?: string | null
}

export function getCoverPageBackgroundImageInput(
  resource: CoverPageBackgroundResource
) {
  return resource.background_image_raw || ""
}

export function getCoverPagePristineBackgroundImage(
  resource: CoverPageBackgroundResource
) {
  return (
    resource.pristine_background ||
    resource.background_image_raw ||
    resource.background_image ||
    null
  )
}

export function isCoverPageResolvedBackgroundImageUrl(
  resource: CoverPageBackgroundResource,
  image?: string | null
) {
  if (!image) return false

  return [
    resource.background_image,
    resource.cover_background_image,
    resource.pristine_background,
    resource.blurred_background,
    resource.cover_blurred_background
  ].some((resolvedImage) => resolvedImage === image)
}

export function getCoverPageDisplayImage(resource?: {
  cover_display_image?: string | null
  cover_image?: string | null
  image_raw?: string | null
} | null) {
  return (
    resource?.cover_display_image ||
    resource?.cover_image ||
    resource?.image_raw ||
    ""
  )
}

export function useCoverPagePanelDebouncedValue<T>(
  value: T,
  delay = COVER_PAGE_PANEL_SEARCH_DEBOUNCE_MS
) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => window.clearTimeout(timeoutId)
  }, [delay, value])

  return debouncedValue
}

function getCoverPagePanelQueryPath(panelPath?: string) {
  const normalizedPath = panelPath?.replace(/^\//, "").replace(/\/$/, "")
  const routePath =
    normalizedPath === "offers/cohort" ? "cohort" : normalizedPath

  return routePath
    ? `/${COVER_PAGE_PANEL_ROOT_KEY}/${routePath}`
    : `/${COVER_PAGE_PANEL_ROOT_KEY}`
}

/** Query-string fragment for the augmentation panel (legacy string form). */
export function getCoverPagePanelPath(panelPath?: string) {
  const queryPath = getCoverPagePanelQueryPath(panelPath)

  return `?${AUGMENTATION_PANEL_QUERY_PARAM}=${queryPath}`
}

function getCoverPagePanelRelativePath(
  section: CoverPagePanelSectionKey,
  id?: number
): string | undefined {
  switch (section) {
    case "offers":
      return id ? `${id}` : undefined
    case "categories":
    case "contributors":
      return id ? `${section}/${id}/` : undefined
    case "header":
    case "preview":
    case "description":
      return section
    default:
      return undefined
  }
}

/** `?panel=` search for a cover design panel section (no hash; panel routes only). */
export function getCoverPagePanelSearchFromSection(
  section: CoverPagePanelSectionKey,
  id?: number
): string {
  const relativePath = getCoverPagePanelRelativePath(section, id)

  return `?${new URLSearchParams({
    [AUGMENTATION_PANEL_QUERY_PARAM]: getCoverPagePanelQueryPath(relativePath)
  }).toString()}`
}

/**
 * Full cover page edit link: pathname + panel query + optional scroll hash on the
 * main URL (hash is never part of the `panel` query param value).
 */
export function getCoverPagePanelSectionLinkTo(
  pathname: string,
  section: CoverPagePanelSectionKey,
  id?: number
): LinkTo {
  const scrollHash = formatCoverPageScrollHash(section)

  return {
    pathname,
    search: getCoverPagePanelSearchFromSection(section, id),
    ...(scrollHash ? { hash: scrollHash } : {})
  }
}

/** Build a URL hash fragment for scrolling to a cover page section (e.g. `#offers`). */
export function formatCoverPageScrollHash(
  sectionKey?: CoverPagePanelSectionKey | CoverPageHashSectionKey | null
): string {
  if (!sectionKey) return ""

  return `#${toCoverPageScrollHashKey(sectionKey)}`
}

/**
 * Parse `#offers`, `offers`, or design-panel keys like `categories` into a
 * cover page scroll/hash target (DOM element id). Panel-only keys normalize via
 * {@link toCoverPageScrollHashKey} (e.g. `categories` → `more`).
 */
export function parseCoverPageScrollHash(
  hash?: string | null
): CoverPageHashSectionKey | null {
  const sectionKey = hash?.replace(/^#/, "")
  if (!sectionKey) return null

  if (
    COVER_PAGE_HASH_SECTION_KEYS.includes(sectionKey as CoverPageHashSectionKey)
  ) {
    return sectionKey as CoverPageHashSectionKey
  }

  if ((Object.values(CoverPagePanelSection) as string[]).includes(sectionKey)) {
    return toCoverPageScrollHashKey(sectionKey as CoverPagePanelSectionKey)
  }

  return null
}

function normalizeCoverPagePathname(pathname: string) {
  return withTrailingSlash(pathname || "/")
}

export function getCoverPagePanelBackToUrl(
  pathname: string,
  section: CoverPagePanelSectionKey
): string {
  return (
    getUrlFromLinkTo(
      getCoverPagePanelSectionLinkTo(
        normalizeCoverPagePathname(pathname),
        section
      )
    ) || normalizeCoverPagePathname(pathname)
  )
}

export function getManageLibraryRouteWithBackTo(
  managePath: string,
  backTo: string,
  params: Record<string, string | undefined> = {}
): string {
  const searchParams = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    if (value) {
      searchParams.set(key, value)
    }
  }

  searchParams.set("backto", backTo)

  return `${managePath}?${searchParams.toString()}`
}
